import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient, type Query, type QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { get, post } from '../api';
import { data, dinheiro, hora, mesAno, nomePessoa, numero, type Idioma } from '../formato';
import { criarAgrupador, deveRepetir, ehSobrecarga, ErroEmRecuo, Recuo, type Agrupador } from '../recuo';
import { useSinal, useTempoRealConectado } from '../tempo-real';
import { novidadePorId } from '../../novidades';

/** Fase 2 (W3): tipos, consultas e texto das notificações (sino e central). */
export type Categoria = 'financeiro' | 'comercial' | 'operacao' | 'pessoas' | 'sistema';
export const CATEGORIAS: readonly Categoria[] = ['financeiro', 'comercial', 'operacao', 'pessoas', 'sistema'];
export type Som = 'entrada' | 'saida' | 'alerta' | 'aviso' | 'padrao';

export interface Notificacao {
  id: string;
  tipo: string;
  categoria: Categoria;
  som: Som;
  link: string | null;
  parametros: Record<string, unknown>;
  lida_em: string | null;
  created_at: string;
}
export interface Opcoes {
  som: boolean;
  novidades_popup: boolean;
  novidades_vistas: string[];
  usuario_criado_em: string | null;
  push: { chave_publica: string | null };
}

const quantidade = (v: unknown) => (typeof v === 'number' ? numero(v, Number.isInteger(v) ? 0 : 2) : String(v ?? ''));

/**
 * Frase no idioma a partir do tipo e dos parâmetros estruturados. Valor passa por `dinheiro` (some com o modo discreto),
 * data e hora pelos formatadores do app; o servidor nunca manda texto pronto.
 */
export function textoDaNotificacao(t: TFunction, n: Pick<Notificacao, 'tipo' | 'parametros'>, idioma: Idioma): { titulo: string; corpo: string } {
  const p = n.parametros ?? {};
  const chave = `notificacoes.tipos.${n.tipo}`;
  const titulo = t(`${chave}.titulo`, { defaultValue: n.tipo });
  if (n.tipo === 'nucleo.novidade') return { titulo, corpo: novidadePorId(String(p.novidade))?.textos[idioma].titulo ?? '' };
  return {
    titulo,
    corpo: t(`${chave}.corpo`, {
      defaultValue: '',
      numero: String(p.numero ?? ''),
      valor: typeof p.valor_centavos === 'number' ? dinheiro(p.valor_centavos, typeof p.moeda === 'string' ? p.moeda : 'BRL') : '',
      vencimento: typeof p.vencimento === 'string' ? data(p.vencimento) : '',
      inicio: typeof p.inicio === 'string' ? hora(p.inicio) : '',
      canal: p.canal === 'instagram' ? 'Instagram' : p.canal === 'email' ? t('notificacoes.canalEmail') : 'WhatsApp',
      codigo: String(p.codigo ?? ''),
      saldo: quantidade(p.saldo),
      minimo: quantidade(p.estoque_minimo),
      // Fase 2 (W20): quem escreveu no chat da equipe (menção, direta, canal) passa pelo modo discreto (iniciais com a opção de nomes).
      autor: nomePessoa(typeof p.autor === 'string' ? p.autor : ''),
      // Fase 2 (W22): a contagem do plural é a quantidade ou, no atraso e no fim do contrato, os dias.
      count: Number(p.quantidade ?? p.dias ?? 0),
      // Fase 2 (W22, K8): exportação, pedido atrasado, folha fechada, conector com erro, acesso e contrato perto do fim.
      relatorio: typeof p.relatorio === 'string' ? t(`relatorios:abas.${p.relatorio}`, { defaultValue: p.relatorio }) : '',
      prazo: typeof p.prazo === 'string' ? data(p.prazo) : '',
      termino: typeof p.termino === 'string' ? data(p.termino) : '',
      competencia: typeof p.competencia === 'string' ? mesAno(p.competencia) : '',
      conector: typeof p.conector === 'string' ? t(`integracoes:conectores.tipos.${p.conector}.nome`, { defaultValue: p.conector }) : '',
      situacao: t(`notificacoes.situacaoConector.${p.estado === 'expirado' ? 'expirado' : 'erro'}`),
      nome: String(p.nome ?? ''),
      // Fase 2 (W12): reajuste de contrato pendente.
      aniversario: typeof p.aniversario === 'string' ? data(p.aniversario) : '',
      acumulado: typeof p.acumulado_pct === 'number' ? `${numero(p.acumulado_pct, 2)}%` : '',
      indice: ({ igpm: 'IGP-M', ipca: 'IPCA', inpc: 'INPC', selic: 'SELIC' } as Record<string, string>)[String(p.indice)] ?? '',
      // Fase 2 (W11): nome do assistente (dado da pessoa, sem valor nem documento) e o alerta de anomalia.
      assistente: String(p.assistente ?? ''),
      quantidade: String(p.quantidade ?? ''),
      minutos: String(p.minutos ?? ''),
      // CRM novo desenho, lote 0: o rótulo do botão que a pessoa usava quando a tela deu erro.
      acao: String(p.acao ?? ''),
      // val1 (28/10): o aviso de segurança da conta sem e-mail (o evento no idioma da tela, com o aparelho quando houver).
      // Lista de 30/10 (item 11): o pedido de troca de e-mail ou nome de usuário (para quem aprova) e a resposta (para quem pediu).
      tipoAcesso: t(`notificacoes.tiposAcesso.${p.pedido === 'email' ? 'email' : 'usuario'}`),
      respostaAcesso: t(`notificacoes.respostasAcesso.${p.resultado === 'aprovado' ? 'aprovado' : 'recusado'}_${p.pedido === 'email' ? 'email' : 'usuario'}`),
      eventoConta: typeof p.evento === 'string' ? t(`notificacoes.eventosConta.${p.evento}`, { defaultValue: '', aparelho: String(p.aparelho ?? '') }) : '',
    }),
  };
}

/**
 * Fase 2 (W16, review r16 robustez M6): um recuo só para as consultas de fundo das notificações (o balde do servidor é por
 * usuário). Depois de um 429 ou 503, contador, listas e opções esperam (exponencial ou `Retry-After`) antes de perguntar
 * de novo; durante a espera a consulta nem sai.
 */
export const recuoNotificacoes = new Recuo();

/** GET das notificações pelo recuo. */
export const buscarNotificacoes = <T>(caminho: string, params?: Record<string, unknown>) => recuoNotificacoes.executar(() => get<T>(caminho, params));

/** O que um aviso novo muda: contador, as recentes do sino e a caixa da central. Opções, preferências e aparelhos, não. */
const MUDAM_COM_AVISO = new Set(['contador', 'recentes', 'caixa']);
export const mudaComAviso = (q: Pick<Query, 'queryKey'>) => q.queryKey[0] === 'notificacoes' && MUDAM_COM_AVISO.has(String(q.queryKey[1]));

const agrupadores = new WeakMap<QueryClient, { agrupador: Agrupador; usos: number }>();

/** Agrupador de sinais por QueryClient: o sino e a central ouvem o mesmo sinal, mas a rajada recarrega uma vez só. */
export function agrupadorDeAvisos(qc: QueryClient) {
  let a = agrupadores.get(qc);
  if (!a) {
    a = { agrupador: criarAgrupador({ executar: () => void qc.invalidateQueries({ predicate: mudaComAviso }), recuo: recuoNotificacoes }), usos: 0 };
    agrupadores.set(qc, a);
  }
  return a;
}

/**
 * Sinal `notificacao` do SSE: a rajada vira UMA recarga (silêncio de 800 ms, no máximo a cada 3 s) do contador e das listas
 * abertas. Com a aba oculta, fica pendente e recarrega uma vez ao voltar; durante o recuo, espera ele acabar.
 */
export function useRecarregarNoSinal() {
  const qc = useQueryClient();
  useEffect(() => {
    const a = agrupadorDeAvisos(qc);
    a.usos += 1;
    return () => {
      a.usos -= 1;
      if (a.usos === 0) {
        a.agrupador.parar();
        agrupadores.delete(qc);
      }
    };
  }, [qc]);
  useSinal('notificacao', () => agrupadorDeAvisos(qc).agrupador.sinal());
}

/**
 * Intervalo do contador: no recuo, a próxima pergunta é quando ele acaba; com o SSE de pé, nenhum (o sinal avisa); sem
 * SSE, 30 s. Com a aba oculta o React Query não repete (`refetchIntervalInBackground` desligado).
 */
export function intervaloDoContador(erro: unknown, conectado: boolean): number | false {
  if (erro instanceof ErroEmRecuo || ehSobrecarga(erro)) return Math.max(recuoNotificacoes.restante(), 1_000);
  return conectado ? false : 30_000;
}

/** Não lidas (o selo) e lidas (CRM novo desenho, lote 0: o pop-up sem novas diz quantas estão na central). */
export interface Contador {
  nao_lidas: number;
  lidas?: number;
}

/** Contador do sino: tempo real quando há SSE; senão polling de 30 s; no 429 e no 503, o recuo. */
export const opcoesContador = (conectado: boolean) => ({
  queryKey: ['notificacoes', 'contador'],
  queryFn: () => buscarNotificacoes<Contador>('/notificacoes/contador'),
  refetchInterval: (q: Query<Contador, Error, Contador, string[]>) => intervaloDoContador(q.state.error, conectado),
  retry: deveRepetir,
});

export function useContador() {
  const conectado = useTempoRealConectado();
  return useQuery(opcoesContador(conectado));
}

export function useRecentes(ativo: boolean) {
  return useQuery({ queryKey: ['notificacoes', 'recentes'], queryFn: () => buscarNotificacoes<{ itens: Notificacao[] }>('/notificacoes', { limite: 8 }), enabled: ativo, retry: deveRepetir });
}

/** Opções da pessoa (som, pop-up de novidades, chave do push): uma vez por carga; o sinal de aviso não as recarrega. */
export function useOpcoes() {
  return useQuery({ queryKey: ['notificacoes', 'opcoes'], queryFn: () => buscarNotificacoes<Opcoes>('/notificacoes/opcoes'), staleTime: 300_000, retry: deveRepetir });
}

/** CRM novo desenho, lote 0: as não lidas do pop-up do sino (marcar como lida tira dali; o sinal de aviso recarrega). */
export function useNaoLidas(ativo: boolean) {
  return useQuery({ queryKey: ['notificacoes', 'recentes', 'nao_lidas'], queryFn: () => buscarNotificacoes<{ itens: Notificacao[] }>('/notificacoes', { limite: 30, nao_lidas: true }), enabled: ativo, refetchOnMount: 'always', retry: deveRepetir });
}

export function useMarcarNaoLidas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (e: { ids: string[] }) => post('/notificacoes/nao-lidas', e),
    onSuccess: () => qc.invalidateQueries({ predicate: mudaComAviso }),
  });
}

export function useMarcarLidas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (e: { ids?: string[]; todas?: boolean }) => post('/notificacoes/lidas', e),
    onSuccess: () => qc.invalidateQueries({ predicate: mudaComAviso }),
  });
}

/** Texto para comparar na busca: sem acento e sem diferença de maiúsculas. */
const comparavel = (v: string) =>
  v
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .toLowerCase();

export type TextosTipos = Record<string, Record<string, { titulo?: string; corpo?: string }>>;

/**
 * Lote de 21/10 (item 3): tipos cujo título ou a parte fixa do texto (sem os `{{campos}}`), no idioma da tela, contêm a busca.
 * O servidor junta com os avisos cujos valores citados (nome, número) casam.
 */
export function tiposDaBusca(textos: TextosTipos | string, busca: string): string[] {
  const termo = comparavel(busca.trim());
  if (!termo || typeof textos !== 'object') return [];
  const saida: string[] = [];
  for (const [modulo, tipos] of Object.entries(textos)) {
    if (!tipos || typeof tipos !== 'object') continue;
    for (const [tipo, x] of Object.entries(tipos)) {
      const fixo = `${x?.titulo ?? ''} ${(x?.corpo ?? '').replace(/\{\{[^}]*\}\}/g, ' ')}`;
      if (comparavel(fixo).includes(termo)) saida.push(`${modulo}.${tipo}`);
    }
  }
  return saida.slice(0, 200);
}
